/**
 * Minigame Playground — Demo scenario builders for testing minigames
 * from the Debug Menu without polluting game state.
 */

import { FISH_DEFINITIONS, BALANCE_CONFIG } from '@/data';
import { Quality, QualityColors, QualityNames, QualityMultipliers } from '@/types';
import type { CollectScenarioConfig } from '@/components/collect';
import type { CollectItemData } from '@/components/collect';
import type { WormDigConfig, WormDigResult } from '@/types';

/** Quality pool for demo items — weighted toward common/uncommon with some rares */
const DEMO_QUALITIES: Quality[] = [
  Quality.COMMON,
  Quality.COMMON,
  Quality.UNCOMMON,
  Quality.UNCOMMON,
  Quality.RARE,
  Quality.EPIC,
  Quality.LEGENDARY,
];

/**
 * Build a demo CollectScenarioConfig using real fish definitions.
 * Items are visual-only — no inventory or currency side effects.
 */
export function buildDemoCollectScenario(
  onComplete: () => void,
): CollectScenarioConfig {
  const shuffled = [...FISH_DEFINITIONS].sort(() => Math.random() - 0.5);
  const count = 5 + Math.floor(Math.random() * 4); // 5-8 items
  const selected = shuffled.slice(0, count);

  const items: CollectItemData[] = selected.map((def, index) => {
    const quality = DEMO_QUALITIES[index % DEMO_QUALITIES.length];
    const value = Math.round(def.baseValue * QualityMultipliers[quality]);

    return {
      id: `demo-collect-${index}`,
      emoji: def.emoji ?? '🐟',
      label: def.name,
      sublabel: QualityNames[quality],
      sublabelColor: QualityColors[quality],
      borderColor: QualityColors[quality],
      glowColor: QualityColors[quality],
      value,
      size: 64,
      restitution: 0.35,
    };
  });

  return {
    items,
    title: 'Demo Collect!',
    spawnOrigin: { x: 0.5, y: -0.05 },
    throwConfig: {
      minVX: -280,
      maxVX: 280,
      minVY: 100,
      maxVY: 450,
      minAngular: -200,
      maxAngular: 200,
      staggerDelayMs: 80,
    },
    autoCollectMs: 8000,
    onComplete,
  };
}

/**
 * Build a demo WormDigConfig for testing the worm dig minigame.
 * Demo only — no bait awarded to inventory.
 */
export function buildDemoWormDigScenario(
  onComplete: (result: WormDigResult) => void,
): WormDigConfig {
  const { WORM_DIG_MIN_WORMS, WORM_DIG_MAX_WORMS } = BALANCE_CONFIG;
  const wormCount = WORM_DIG_MIN_WORMS +
    Math.floor(Math.random() * (WORM_DIG_MAX_WORMS - WORM_DIG_MIN_WORMS + 1));

  return {
    wormCount,
    revealThreshold: BALANCE_CONFIG.WORM_DIG_REVEAL_THRESHOLD,
    digRadius: BALANCE_CONFIG.WORM_DIG_BRUSH_RADIUS,
    wormHitRadius: BALANCE_CONFIG.WORM_DIG_WORM_HIT_RADIUS,
    maxParticles: BALANCE_CONFIG.WORM_DIG_MAX_PARTICLES,
    timeoutMs: BALANCE_CONFIG.WORM_DIG_TIMEOUT_MS,
    wormsPerFind: BALANCE_CONFIG.WORM_DIG_BAIT_PER_WORM,
    onComplete,
  };
}
